import React, { useCallback, useEffect, useState } from 'react';
import { BellRing, BellOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { smsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

// Super-admin only. ON means this org is on the force-real-send list, so its
// texts go to real phones; OFF means no override (demo orgs stay suppressed).
export const DemoNotificationsToggle = ({ organizationId, organizationName }) => {
    const [on, setOn] = useState(null);
    const [busy, setBusy] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const refresh = useCallback(async () => {
        if (!organizationId || organizationId === 'all') { setOn(null); return; }
        try {
            const { data } = await smsAPI.status();
            const forced = (data?.demo_suppression?.force_real_send_orgs || []).map((o) => o.id);
            setOn(forced.includes(organizationId));
        } catch {
            setOn(null);
        }
    }, [organizationId]);

    useEffect(() => { refresh(); }, [refresh]);

    const apply = async (mode) => {
        setBusy(true);
        try {
            const { data } = await smsAPI.setSuppression(organizationId, mode);
            setOn(mode === 'allow_real');
            toast.success(mode === 'allow_real'
                ? `Real texts ON for ${data?.name || organizationName || 'this organization'}`
                : `Real texts OFF for ${data?.name || organizationName || 'this organization'}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not change notification mode');
            refresh();
        } finally {
            setBusy(false);
            setConfirmOpen(false);
        }
    };

    if (on === null || !organizationId || organizationId === 'all') return null;

    return (
        <>
            <Button
                variant="outline"
                disabled={busy}
                onClick={() => (on ? apply('default') : setConfirmOpen(true))}
                data-testid="cc-demo-notifications-toggle"
                className={`gap-2 border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/60 ${on ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'}`}
            >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" />
                    : on ? <BellRing className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                <span className="text-sm">Demo Notifications:{' '}
                    <span className="font-semibold" data-testid="cc-demo-notifications-state">{on ? 'ON' : 'OFF'}</span>
                </span>
            </Button>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid="cc-demo-notifications-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-base text-slate-900 dark:text-white">Turn on real texts?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This organization will now send real texts to real phone numbers.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cc-demo-notifications-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); apply('allow_real'); }}
                            data-testid="cc-demo-notifications-confirm-btn"
                        >
                            Turn on
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default DemoNotificationsToggle;
